/**
 * Hooks local-first de la ESPINA de módulos (F2-A): módulos, ajustes y
 * recursos de un curso. Mismo cableado fino que use-academy-data.
 */
import type { Accessor } from "solid-js";
import {
  getCourseModulesQuery,
  getCourseResourcesQuery,
  getCourseSettingsQuery,
  moduleQueryKeys,
} from "@space/shared";
import { createPowerSyncQuery, createPowerSyncSingle } from "@space/shared/solid";
import type { CourseModule, CourseSettings, ResourceRow } from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  return { db, drizzle, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/** Módulos (espina) de un curso, ordenados por posición. */
export function useCourseModules(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CourseModule>({
    queryKey: () => moduleQueryKeys.byCourse(courseId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = courseId();
      return db && id ? getCourseModulesQuery(db, id) : null;
    },
    enabled: () => Boolean(courseId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Ajustes (singleton) de un curso. */
export function useCourseSettings(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<CourseSettings>({
    queryKey: () => moduleQueryKeys.settings(courseId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = courseId();
      return db && id ? getCourseSettingsQuery(db, id) : null;
    },
    enabled: () => Boolean(courseId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Recursos de un curso. */
export function useCourseResources(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<ResourceRow>({
    queryKey: () => moduleQueryKeys.resources(courseId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = courseId();
      return db && id ? getCourseResourcesQuery(db, id) : null;
    },
    enabled: () => Boolean(courseId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}
